import React, { Component } from 'react';
import {
  Text, View, StyleSheet, TouchableOpacity, Platform,
} from 'react-native';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { workoutProgramWithDescriptions } from 'fitbeat.gym-core';
import { FoodPreference, ProgramType } from 'fitbeat.models';
import { BaseModal, toggleNutritionStepsModal } from '../ui';
import { nutritionPreferenceChange, savePreferences } from './actions';
import { UserDataConsumer } from '../utils';
import { Icon } from '../components';
import { getNutritionPreferenceText } from '../utils/WorkoutSettingsTextHelpers';
import { DietaryOptionsDropdown, NutritionGuide } from './components';
import Theme from '../theme';
import { GoalCardWithFullDescription } from '../Bookings/components';
import { mappedTitles, mappedIcons } from '../WorkoutGoalAssistant/utils';
import { defaultScale } from '../utils/scaling';
import { enabledFeaturesSelector } from '../auth';
import { IEnabledFeatures } from 'fitbeat.models';
import { TranslateProp } from '../common-types';

interface IDietaryOption {
  value: FoodPreference;
  display: string;
}

interface IProps {
  foodPreference?: FoodPreference;
  nutritionPreference?: { foodPreference: FoodPreference };
  toggleNutritionStepsModal: (visible: boolean) => void;
  user: {
    userProfile: {
      useImperialUnit: boolean;
      longTermGoal: string;
      gender: string;
    };
    nutritionService: any;
  };
  enabledFeatures: IEnabledFeatures;
  savePreferences: (foodPreference: FoodPreference, useImperialUnit: boolean, dietGoal: string, gender: string) => void;
  isNutritionStepsModalVisible: boolean;
  t: TranslateProp;
}

interface IState {
  nutritionPreferenceVisible: boolean;
  selectedNutritionPreference: { value: FoodPreference; display: string } | null;
  dietaryOptions: Array<{ value: FoodPreference; display: string }>;
}

const noOp = () => {/* intentionally empty */};

class NutritionStepsModalComponent extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      nutritionPreferenceVisible: false,
      selectedNutritionPreference: props.foodPreference ? {
        value: props.foodPreference,
        display: getNutritionPreferenceText[props.foodPreference].toUpperCase(),
      } : null,
      dietaryOptions: [],
    };
  }

  public componentDidMount() {
    const { nutritionPreference } = this.props;
    const options = this.getDietaryOptions();
    const selectedOption = options.find(
      (singleItem) => nutritionPreference && singleItem.value === nutritionPreference.foodPreference,
    );

    this.setState({
      dietaryOptions: options,
      selectedNutritionPreference: selectedOption || null,
    });
  }

  public componentDidUpdate(prevProps: IProps) {
    const { nutritionPreference } = this.props;
    if (nutritionPreference !== prevProps.nutritionPreference) {
      const options = this.getDietaryOptions();
      const selectedOption = options.find(
        (singleItem) => nutritionPreference?.foodPreference && singleItem.value === nutritionPreference.foodPreference,
      );
      this.setState({ selectedNutritionPreference: selectedOption || null });
    }
  }

  public closeMealModal = (): void => {
    this.props.toggleNutritionStepsModal(false);
  };

  public findWorkoutTypes = () => {
    const { user: { userProfile }, enabledFeatures } = this.props;
    const userLongTermGoal = !userProfile || userProfile.longTermGoal === 'NotSet'
      ? ProgramType.GetStrongAndFitGoal
      : userProfile.longTermGoal;
    let title;
    let icon;
    const workoutTypes = workoutProgramWithDescriptions(enabledFeatures)
      .filter(({ programType }) => programType !== ProgramType.NotSet).find(({ programType, summary }) => {
        title = mappedTitles[programType];
        icon = mappedIcons[programType]({}, Theme.BLACK);
        return programType === userLongTermGoal;
      });
    return { workoutTypes, title, icon };
  };

  public changePreference = async (selectedPreference: IDietaryOption): Promise<void> => {
    const { user: { userProfile } } = this.props;
    this.setState({ selectedNutritionPreference: selectedPreference });
    await this.props.savePreferences(
      selectedPreference.value,
      !!userProfile.useImperialUnit,
      userProfile.longTermGoal,
      userProfile.gender,
    );
    this.toggleNutritionOptions();
    this.props.toggleNutritionStepsModal(false);
  };

  public toggleNutritionOptions = (): void => {
    const { nutritionPreferenceVisible } = this.state;
    this.setState({ nutritionPreferenceVisible: !nutritionPreferenceVisible });
  };

  public getDietaryOptions = () => {
    const dietaryOptions = Object.values(FoodPreference).map((item) => ({
      value: item,
      display: getNutritionPreferenceText[item].toUpperCase(),
    }));

    return dietaryOptions.filter(
      (singleItem) => singleItem.value !== FoodPreference.HighProtein,
    );
  };

  public render() {
    const {
      t,
      isNutritionStepsModalVisible,
      user: { userProfile },
    } = this.props;
    const goalData = this.findWorkoutTypes();
    const {
      nutritionPreferenceVisible,
      selectedNutritionPreference,
      dietaryOptions,
    } = this.state;

    return (
      <BaseModal
        title={t('fitillionNutrition')}
        closeModal={this.closeMealModal}
        hideCloseButton={Platform.OS === 'web'}
        containerStyle={{ height: '100vh' }}
        headerTextStyle={{ paddingBottom: defaultScale(10) }}
        isModalVisible={isNutritionStepsModalVisible}
      >
        <View>
          <View style={styles.horizontalSeparator} />
          <View style={[styles.goalAndPreferenceContainer]}>
            {goalData
                                && (
                                <View style={[styles.goalDisplaySection]}>
                                  <GoalCardWithFullDescription
                                    onPress={noOp}
                                    withArrow={false}
                                    isActive={false}
                                    key={goalData.title}
                                    title={goalData.title}
                                    icon={goalData.icon}
                                    subtitle={t('fitnessGoalNoColon')}
                                    containerStyle={styles.goalCardContainer}
                                    headingTextStyle={styles.goalCardHeadingTextStyle}
                                    subtitleStyle={styles.subtitleStyle}
                                  />
                                </View>
                                )}
            <View style={styles.verticalSeparator} />
            <View style={[styles.nutritionPreferenceContainer]}>
              <Text style={styles.subtitleStyle}>
                {t('Preference')}
                :
              </Text>
              <TouchableOpacity
                onPress={this.toggleNutritionOptions}
                style={styles.dropDownControl}
              >
                <Text style={[
                  styles.dropDownControlText,
                  { color: selectedNutritionPreference ? Theme.BLACK : Theme.GREY_MEDIUM },
                ]}
                >
                  {selectedNutritionPreference ? selectedNutritionPreference.display : 'Select...'}
                </Text>
                <Icon
                  style={styles.chevronIcon}
                  size={defaultScale(25)}
                  color={Theme.PRIMARY}
                  name={nutritionPreferenceVisible ? 'ios-arrow-up-outline' : 'ios-arrow-down-outline'}
                />
              </TouchableOpacity>
            </View>
          </View>
          <View style={{ position: 'relative', width: '100%' }}>
            {nutritionPreferenceVisible && (
            <DietaryOptionsDropdown
              currentlySelected={selectedNutritionPreference}
              options={dietaryOptions}
              changePreference={this.changePreference}
            />
            )}
          </View>
          <View style={styles.horizontalSeparator} />
        </View>
        <NutritionGuide userProfile={userProfile} />
        <View>
          <TouchableOpacity onPress={this.closeMealModal} style={styles.closeTextContainer}>
            <Text style={styles.closeText}>
              {t('close')}
            </Text>
          </TouchableOpacity>
        </View>
      </BaseModal>
    );
  }
}

const styles = StyleSheet.create({
  goalAndPreferenceContainer: {
    flexDirection: 'row',
    zIndex: 99,
    paddingVertical: defaultScale(10),
  },
  goalDisplaySection: {
    width: '49%',
    flexDirection: 'column',
  },
  nutritionPreferenceContainer: {
    width: '49%',
  },
  horizontalSeparator: {
    borderBottomWidth: 1,
    borderBottomColor: Theme.GREY_MEDIUM_LIGHT,
  },
  verticalSeparator: {
    width: 1,
    marginHorizontal: defaultScale(10),
    marginVertical: defaultScale(5),
    backgroundColor: Theme.GREY_MEDIUM_LIGHT,
  },
  goalCardContainer: {
    flex: 0,
    backgroundColor: Theme.WHITE,
    borderWidth: 0,
    padding: 0,
    margin: 0,
    marginVertical: 0,
  },
  goalCardHeadingTextStyle: {
    ...Theme.textStyle.heading,
    color: Theme.BLACK,
    paddingVertical: defaultScale(3),
  },
  subtitleStyle: {
    ...Theme.textStyle.small,
    color: Theme.GREY_DARK,
  },
  dropDownControl: {
    flexDirection: 'row',
    width: '85%',
    justifyContent: 'space-between',
  },
  dropDownControlText: {
    ...Theme.textStyle.heading,
  },
  chevronIcon: {
    paddingHorizontal: defaultScale(5),
    alignSelf: 'center',
  },
  closeTextContainer: {
    alignSelf: 'center',
    justifyContent: 'center',
    height: defaultScale(50),
  },
  closeText: {
    ...Theme.textStyle.heading,
    color: Theme.PRIMARY,
  },
});

const mapStateToProps = (state) => {
  const { nutrition } = state;
  const { nutritionPreference } = nutrition;
  return {
    isNutritionStepsModalVisible: state.ui.nutritionStepsVisible,
    longTermGoal: state.longTermGoal,
    nutritionPreference,
    enabledFeatures: enabledFeaturesSelector(state),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const { user: { nutritionService } } = ownProps;
  return {
    savePreferences: (foodPreference, useImperialUnit, dietGoal, gender) => {
      dispatch(savePreferences(nutritionService, foodPreference, useImperialUnit, dietGoal, gender));
    },
    toggleNutritionStepsModal: (visible) => dispatch(toggleNutritionStepsModal(visible)),
    nutritionPreferenceChange: (selectedPreference) => dispatch(nutritionPreferenceChange(selectedPreference)),
  };
};

const NutritionStepsModal = UserDataConsumer(
  connect(mapStateToProps, mapDispatchToProps)(NutritionStepsModalComponent));
export default translate(['nutrition'], { wait: true })(NutritionStepsModal);
